import { supabase } from '@core/auth';

export type BetaAccessSource = 'open' | 'env' | 'table' | 'denied' | 'error';

export interface BetaAccessResult {
  allowed: boolean;
  source: BetaAccessSource;
  reason?: string;
}

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const parseEnvAllowlist = (): Set<string> => {
  const raw = (import.meta.env.VITE_BETA_ALLOWLIST_EMAILS as string | undefined) ?? '';
  return new Set(
    raw
      .split(',')
      .map((value) => normalizeEmail(value))
      .filter(Boolean)
  );
};

export const isClosedBetaEnabled = (): boolean => {
  return String(import.meta.env.VITE_BETA_CLOSED ?? '0') === '1';
};

export async function checkBetaAccess(email: string): Promise<BetaAccessResult> {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    return { allowed: false, source: 'denied', reason: 'Email vacío.' };
  }

  if (!isClosedBetaEnabled()) {
    return { allowed: true, source: 'open' };
  }

  const envAllowlist = parseEnvAllowlist();
  if (envAllowlist.has('*') || envAllowlist.has(normalizedEmail)) {
    return { allowed: true, source: 'env' };
  }

  if (!supabase) {
    return {
      allowed: false,
      source: 'error',
      reason: 'Supabase no está configurado para validar acceso progresivo.',
    };
  }

  try {
    const { data, error } = await supabase
      .from('beta_testers')
      .select('email')
      .ilike('email', normalizedEmail)
      .limit(1);

    if (error) {
      return { allowed: false, source: 'error', reason: error.message };
    }

    if ((data ?? []).length > 0) {
      return { allowed: true, source: 'table' };
    }

    return {
      allowed: false,
      source: 'denied',
      reason: 'Tu cuenta todavía no tiene acceso a esta función en la beta pública.',
    };
  } catch (error: any) {
    return {
      allowed: false,
      source: 'error',
      reason: error?.message || 'No se pudo validar el acceso progresivo.',
    };
  }
}
